/**
 * VisionForge endpoint + shell mode.
 *
 * Catalog Connect (HF / Cloudflare / CivitAI / Forge endpoints) is the customer path.
 * Deep link: ?endpoint=…&kind=openai-vision&mode=ask stored as custom.
 */

import type { ConnectionTab } from "@forgesuite/gpu-browser";

export type VisionBindKind = "openai-vision" | "vlm" | "screen-agent" | string;

export type VisionSource = ConnectionTab | "custom";

export type VisionEndpoint = {
  bindKind: VisionBindKind;
  endpointUrl: string;
  label?: string;
  source: VisionSource;
  model?: string;
  apiKey?: string;
};

export type ShellMode = "live" | "ask" | "screen" | ConnectionTab;

const CATALOG_TABS: ConnectionTab[] = ["production", "hf", "cloudflare", "civitai", "forge_endpoint"];
const SHELL_MODES: ShellMode[] = ["live", "ask", "screen", ...CATALOG_TABS];

function isConnectionTab(v: string | null | undefined): v is ConnectionTab {
  return !!v && (CATALOG_TABS as string[]).includes(v);
}

function isShellMode(v: string | null | undefined): v is ShellMode {
  return !!v && (SHELL_MODES as string[]).includes(v);
}

function normalizeSource(raw: string | undefined): VisionSource {
  if (isConnectionTab(raw) || raw === "custom") return raw;
  return "custom";
}

const EP_KEY = "visionforge.endpoint.v1";
const SHELL_KEY = "visionforge.shell_mode.v1";

export function normalizeEndpointUrl(raw: string): string {
  const u = (raw || "").trim().replace(/\/$/, "");
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) return `https://${u}`;
  return u;
}

export function loadEndpoint(): VisionEndpoint | null {
  try {
    const raw = localStorage.getItem(EP_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as VisionEndpoint;
    if (!j?.endpointUrl) return null;
    return { ...j, source: normalizeSource(j.source) };
  } catch {
    return null;
  }
}

export function saveEndpoint(ep: VisionEndpoint | null): void {
  try {
    if (!ep) localStorage.removeItem(EP_KEY);
    else localStorage.setItem(EP_KEY, JSON.stringify({ ...ep, source: normalizeSource(ep.source) }));
  } catch {
    /* ignore */
  }
}

export function isCatalogShell(m: ShellMode): m is ConnectionTab {
  return isConnectionTab(m);
}

export function loadShellMode(): ShellMode {
  try {
    const v = localStorage.getItem(SHELL_KEY);
    if (v === "inference" || v === "vgpu") return "cloudflare";
    if (isShellMode(v)) return v;
    const q = new URLSearchParams(window.location.search).get("mode");
    if (q === "inference" || q === "vgpu") return "cloudflare";
    if (isShellMode(q)) return q;
  } catch {
    /* ignore */
  }
  return "live";
}

export function saveShellMode(m: ShellMode): void {
  try {
    localStorage.setItem(SHELL_KEY, m);
  } catch {
    /* ignore */
  }
}

/** Consume ?endpoint=&kind=&mode= from an external handoff. Stored as custom. */
export function consumeBinderQuery(): VisionEndpoint | null {
  try {
    const sp = new URLSearchParams(window.location.search);
    const endpoint = sp.get("endpoint") || sp.get("url");
    const kind = sp.get("kind") || sp.get("bindKind") || "openai-vision";
    const mode = sp.get("mode");
    if (mode === "inference" || mode === "vgpu") saveShellMode("cloudflare");
    else if (isShellMode(mode)) saveShellMode(mode);
    if (!endpoint) return loadEndpoint();
    const ep: VisionEndpoint = {
      bindKind: kind,
      endpointUrl: normalizeEndpointUrl(endpoint),
      source: "custom",
      label: "Handoff endpoint",
    };
    saveEndpoint(ep);
    if (kind.includes("vision") || kind.includes("vlm") || kind.includes("screen")) {
      if (!mode || mode === "inference" || mode === "vgpu") {
        saveShellMode(kind.includes("screen") ? "screen" : "ask");
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("endpoint");
    url.searchParams.delete("url");
    url.searchParams.delete("kind");
    url.searchParams.delete("bindKind");
    url.searchParams.delete("mode");
    history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    return ep;
  } catch {
    return loadEndpoint();
  }
}

export async function probeEndpointHealth(
  ep: VisionEndpoint,
): Promise<{ ok: boolean; message: string }> {
  const base = ep.endpointUrl.replace(/\/$/, "");
  const paths = ["/health", "/v1/models", "/"];
  for (const p of paths) {
    try {
      const res = await fetch(`${base}${p === "/" ? "" : p}`, {
        method: "GET",
        signal: AbortSignal.timeout(8000),
      });
      if (res.ok || res.status === 404) {
        return { ok: res.ok || res.status < 500, message: `HTTP ${res.status} @ ${p}` };
      }
    } catch (e) {
      if (p === paths[paths.length - 1]) {
        return { ok: false, message: e instanceof Error ? e.message : String(e) };
      }
    }
  }
  return { ok: false, message: "No health response" };
}
